import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const getOptimizedUrl = (url, width = 400) => {
  if (!url || !url.includes('cloudinary.com') || !url.includes('/upload/')) return url;
  const [base, resto] = url.split('/upload/');
  const segmentos = resto.split('/');
  // Solo el PRIMER segmento puede ser una transformación previa.
  // Se identifica porque contiene parámetros conocidos de Cloudinary
  // (w_, h_, c_, q_, f_, e_, g_, b_, ar_) — nunca tocamos carpetas ni nombres de archivo.
  const esTransformacion = /(^|,)(w_|h_|c_|q_|f_|e_|g_|b_|ar_|dpr_|fl_)/.test(segmentos[0]);
  if (esTransformacion) segmentos.shift();
  return `${base}/upload/w_${width},c_limit,q_auto,f_auto/${segmentos.join('/')}`;
};

const FALLBACK_IMG =
  'data:image/svg+xml;charset=UTF-8,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="300"%3E%3Crect width="300" height="300" fill="%23ffffff"/%3E%3C/svg%3E';

const IconCart = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="21" r="1"/>
    <circle cx="20" cy="21" r="1"/>
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
  </svg>
);

const ProductCard = ({ producto, addToCart, index = 0, vistaAfiliado = false }) => {
  const navigate = useNavigate();
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError]   = useState(false);

  const idReal = producto?.id ?? producto?._id ?? producto?.ID
              ?? producto?.idProducto ?? producto?.productId;

  const tieneOferta =
    producto.precioOferta !== undefined &&
    producto.precioOferta !== null &&
    producto.precioOferta !== '' &&
    Number(producto.precioOferta) > 0 &&
    Number(producto.precioOferta) < Number(producto.precio);

  const tieneDescuento =
    producto.descuento !== undefined &&
    producto.descuento !== null &&
    producto.descuento !== '' &&
    Number(producto.descuento) > 0;

  const esPrioritaria = index < 6;
  const srcOptimizado = imgError
    ? FALLBACK_IMG
    : getOptimizedUrl(producto.imagenUrl, 400) || FALLBACK_IMG;

  // Precio base: precioOferta si activo, sino precio normal
  const precioBase = tieneOferta
    ? Number(producto.precioOferta)
    : Number(producto.precio);

  // Precio que se muestra al cliente normal
  const precioFinalCliente = precioBase.toFixed(2);

  // Precio afiliado: -25% sobre el precioBase
  const precioAfiliadoNum = precioBase * 0.75;
  const precioAfiliadoStr = precioAfiliadoNum.toFixed(2);

  const handleVerDetalle = (e) => {
    e.preventDefault();
    if (!idReal) return;
    navigate(`/producto/${idReal}`, { state: { producto } });
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (addToCart) {
      if (vistaAfiliado) {
        addToCart({ ...producto, precioAfiliado: precioAfiliadoNum });
      } else {
        addToCart(producto);
      }
    }
  };

  return (
    <div
      onClick={handleVerDetalle}
      style={{
        width: '100%',
        backgroundColor: 'var(--color-blanco)',
        borderRadius: 'var(--radius-lg)',
        border: '0.5px solid var(--border-default)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'var(--font-body)',
        overflow: 'hidden',
        /* Altura uniforme entre cards de la misma fila */
        height: '100%',
        transition: 'transform var(--transition-normal), box-shadow var(--transition-normal)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = 'var(--shadow-md)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'var(--shadow-none)';
      }}
    >

      {/* Franja superior */}
      {vistaAfiliado ? (
        <div style={{
          width: '100%', backgroundColor: '#6c3fc5',
          padding: '3px 0', display: 'flex', alignItems: 'center',
          justifyContent: 'center', flexShrink: 0,
        }}>
          <span style={{
            fontSize: 'var(--font-size-label)', fontWeight: 'var(--font-weight-medium)',
            color: 'var(--color-blanco)', letterSpacing: 'var(--letter-spacing-wider)',
            textTransform: 'uppercase', lineHeight: 1.4,
          }}>
            Precio afiliado −25%
          </span>
        </div>
      ) : (tieneOferta || tieneDescuento) && (
        <div style={{
          width: '100%', backgroundColor: 'var(--color-verde)',
          padding: '3px 0', display: 'flex', alignItems: 'center',
          justifyContent: 'center', flexShrink: 0,
        }}>
          <span style={{
            fontSize: 'var(--font-size-label)', fontWeight: 'var(--font-weight-medium)',
            color: 'var(--color-blanco)', letterSpacing: 'var(--letter-spacing-wider)',
            textTransform: 'uppercase', lineHeight: 1.4,
          }}>
            En descuento
          </span>
        </div>
      )}

      {/* Imagen — cuadrada, fondo blanco */}
      <div style={{
        width: '100%', aspectRatio: '1 / 1',
        backgroundColor: 'var(--color-blanco)',
        position: 'relative', overflow: 'hidden', flexShrink: 0,
      }}>
        {/* Skeleton */}
        {!imgLoaded && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(90deg, #f0f0ee 25%, #fff 50%, #f0f0ee 75%)',
            backgroundSize: '200% 100%',
            animation: 'skeleton-loading 1.2s ease-in-out infinite',
          }} />
        )}

        <img
          src={srcOptimizado} alt={producto.nombre}
          loading={esPrioritaria ? 'eager' : 'lazy'}
          fetchpriority={esPrioritaria ? 'high' : 'auto'}
          decoding="async"
          onLoad={() => setImgLoaded(true)}
          onError={() => { setImgError(true); setImgLoaded(true); }}
          style={{
            width: '100%', height: '100%', objectFit: 'contain',
            padding: 'var(--space-1)', opacity: imgLoaded ? 1 : 0,
            transition: 'opacity var(--transition-normal)',
          }}
        />
      </div>

      {/* Cuerpo */}
      <div style={{
        padding: 'var(--space-2) var(--space-3) var(--space-3)',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        gap: 'var(--space-1)', flex: 1,
      }}>
        {/* Nombre */}
        <p style={{
          fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-regular)',
          color: 'var(--text-primary)', margin: 0, textAlign: 'center',
          lineHeight: '1.3', minHeight: 'calc(1.3em * 2)',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden', textDecoration: 'none', fontFamily: 'var(--font-body)',
        }}>
          {producto.nombre}
        </p>

        {/* Precio */}
        <div style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'center',
          gap: 'var(--space-2)', flexWrap: 'wrap', marginTop: 'auto',
        }}>
          {vistaAfiliado ? (
            <>
              {/* Precio base tachado */}
              <span style={{
                fontSize: 'var(--font-size-xs)', color: 'var(--text-disabled)',
                textDecoration: 'line-through', fontWeight: 'var(--font-weight-regular)',
              }}>
                ${precioFinalCliente}
              </span>
              {/* Precio afiliado destacado */}
              <span style={{
                fontFamily: 'var(--font-body)', fontWeight: 'var(--font-weight-medium)',
                fontSize: '0.95rem', color: '#6c3fc5',
                letterSpacing: 'var(--letter-spacing-tight)', lineHeight: 1,
              }}>
                ${precioAfiliadoStr}
              </span>
            </>
          ) : (
            <>
              {tieneOferta && (
                <span style={{
                  fontSize: 'var(--font-size-xs)', color: 'var(--text-disabled)',
                  textDecoration: 'line-through', fontWeight: 'var(--font-weight-regular)',
                }}>
                  ${Number(producto.precio).toFixed(2)}
                </span>
              )}
              <span style={{
                fontFamily: 'var(--font-body)', fontWeight: 'var(--font-weight-medium)',
                fontSize: '0.95rem', color: 'var(--text-primary)',
                letterSpacing: 'var(--letter-spacing-tight)', lineHeight: 1,
              }}>
                ${precioFinalCliente}
              </span>
            </>
          )}
        </div>

      </div>

      {/* Separador + botón outline minimalista */}
      <div style={{
        borderTop: '0.5px solid var(--border-default)',
        padding: 'var(--space-2) var(--space-3) var(--space-3)',
      }}>
        <button
          onClick={handleAddToCart}
          aria-label={`Agregar ${producto.nombre} al carrito`}
          style={{
            width: '100%',
            padding: '8px var(--space-2)',
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'transparent',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-default)',
            cursor: 'pointer',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 'var(--font-weight-medium)',
            fontFamily: 'var(--font-body)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--space-1)',
            transition: 'border-color var(--transition-fast), background-color var(--transition-fast), transform var(--transition-fast)',
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--color-negro)';
            e.currentTarget.style.backgroundColor = 'var(--color-gris-fondo)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--border-default)';
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          onMouseDown={(e)  => e.currentTarget.style.transform = 'scale(0.98)'}
          onMouseUp={(e)    => e.currentTarget.style.transform = 'scale(1)'}
        >
          <IconCart />
          Agregar al carrito
        </button>
      </div>

      <style>{`
        @keyframes skeleton-loading {
          0%   { background-position:  200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
};

export default ProductCard;